import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';

const Nav = styled.nav`
  background: ${props => props.theme.navBackground};
  color: ${props => props.theme.navText};
  padding: ${props => props.compact ? '0.125rem 0.25rem' : '1rem 1.5rem'};
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 1px 3px ${props => props.theme.shadow};
  border-bottom: 1px solid ${props => props.theme.navBorder};
  position: relative;
`;

const NavLinks = styled.div`
  display: flex;
  gap: ${props => props.compact ? '0.25rem' : '1rem'};

  @media (max-width: 768px) {
    position: fixed;
    top: 60px;
    right: 0;
    background: ${props => props.theme.navBackground};
    flex-direction: column;
    padding: ${props => props.compact ? '0.25rem' : '1rem'};
    box-shadow: -2px 2px 8px ${props => props.theme.shadowMedium};
    border-bottom: 1px solid ${props => props.theme.navBorder};
    min-width: 200px;
    transform: ${props => props.$isOpen ? 'translateX(0)' : 'translateX(100%)'};
    transition: transform 0.3s ease;
    z-index: 1000;
  }
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${props => props.compact ? '0.25rem' : '0.5rem'};
  text-decoration: none;
  cursor: pointer;
  
  &:hover h1 {
    color: #7289da;
  }
`;

const LogoIcon = styled.img`
  height: ${props => props.compact ? '1.25rem' : '1.5rem'};
  width: ${props => props.compact ? '1.25rem' : '1.5rem'};
`;

const HamburgerButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${props => props.theme.navText};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  
  @media (max-width: 768px) {
    display: block;
  }

  &:hover {
    color: #5865f2;
  }
`;

const HamburgerIcon = styled.div`
  width: 25px;
  height: 3px;
  background: currentColor;
  position: relative;
  transition: all 0.3s ease;

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 25px;
    height: 3px;
    background: currentColor;
    transition: all 0.3s ease;
  }

  &::before {
    top: -8px;
  }

  &::after {
    top: 8px;
  }

  ${props => props.$isOpen && `
    background: transparent;
    
    &::before {
      transform: rotate(45deg);
      top: 0;
    }

    &::after {
      transform: rotate(-45deg);
      top: 0;
    }
  `}
`;

const Overlay = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
    position: fixed;
    top: 60px;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }
`;

const StyledLink = styled(Link)`
  color: ${props => props.theme.textSecondary};
  text-decoration: none;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  font-weight: 500;

  &:hover {
    background: ${props => props.theme.cardBackgroundHover};
    color: ${props => props.theme.navTextHover};
  }
`;

const LogoutButton = styled.button`
  background: transparent;
  color: ${props => props.theme.textSecondary};
  border: 1px solid ${props => props.theme.border};
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  font-weight: 500;

  &:hover {
    background: ${props => props.theme.buttonDanger};
    color: white;
    border: 1px solid ${props => props.theme.buttonDanger};
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const DropdownButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.textSecondary};
  text-decoration: none;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  height: 100%;
  line-height: 1;

  &:hover {
    background: ${props => props.theme.cardBackgroundHover};
    color: ${props => props.theme.navTextHover};
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: ${props => props.theme.navBackground};
  border: 1px solid ${props => props.theme.navBorder};
  border-radius: 4px;
  box-shadow: 0 4px 12px ${props => props.theme.shadowLarge};
  min-width: 160px;
  z-index: 1001;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s ease;
`;

const DropdownItem = styled(Link)`
  display: block;
  color: ${props => props.theme.textSecondary};
  text-decoration: none;
  padding: 0.75rem 1rem;
  transition: all 0.2s ease;
  border-bottom: 1px solid ${props => props.theme.navBorder};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${props => props.theme.cardBackgroundHover};
    color: ${props => props.theme.navTextHover};
  }
`;

const DropdownIcon = styled.span`
  font-size: 0.75rem;
  transition: transform 0.2s ease;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const Navbar = () => {
  const { isAuthenticated, logout, user } = useAuth();
  const { settings } = useData();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const isPremium = (user?.subscription && user?.subscription !== 'free') || (user?.aiTier && user?.aiTier !== 'free');
  const isCompact = isPremium && settings?.display?.compactMode;

  const handleLogout = async () => {
    setIsMenuOpen(false);
    setIsDropdownOpen(false);
    sessionStorage.setItem('fromLogout', 'true');
    sessionStorage.setItem('logoutTimestamp', Date.now().toString());
    navigate('/');
    logout();
    // Don't clear the flag - let timestamp-based protection handle it
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const closeDropdown = () => {
    setIsDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <Nav compact={isCompact}>
        <LogoContainer to="/dashboard" compact={isCompact}>
          <LogoIcon src="/PlanMySemester1.ico" alt="PlanMySemester" compact={isCompact} />
          <h1 style={{ margin: 0, color: '#5865f2', fontSize: isCompact ? '1.25rem' : '1.5rem', fontWeight: 600 }}>PlanMySemester</h1>
        </LogoContainer>
        {isAuthenticated ? (
          <>
            <HamburgerButton onClick={toggleMenu} aria-label="Toggle menu">
              <HamburgerIcon $isOpen={isMenuOpen} />
            </HamburgerButton>
            <NavLinks $isOpen={isMenuOpen}>
              <StyledLink to="/dashboard" onClick={closeMenu}>Dashboard</StyledLink>
              <StyledLink to="/assignments" onClick={closeMenu}>Coursework</StyledLink>
              <StyledLink to="/calendar" onClick={closeMenu}>Calendar</StyledLink>
              <StyledLink to="/account" onClick={closeMenu}>Account</StyledLink>
              <DropdownContainer ref={dropdownRef}>
                <DropdownButton onClick={toggleDropdown}>
                  More <DropdownIcon $isOpen={isDropdownOpen}>▼</DropdownIcon>
                </DropdownButton>
                <DropdownMenu $isOpen={isDropdownOpen}>
                  <DropdownItem to="/analytics" onClick={closeDropdown}>Analytics</DropdownItem>
                  {/* <DropdownItem to="/ai" onClick={closeDropdown}>AI Assistant</DropdownItem> */}
                  <DropdownItem to="/settings" onClick={closeDropdown}>Settings</DropdownItem>
                  <DropdownItem to="/support" onClick={closeDropdown}>Support</DropdownItem>
                  {user && user.role === 'admin' && (
                    <DropdownItem to="/admin" onClick={closeDropdown}>Admin Settings</DropdownItem>
                  )}
                </DropdownMenu>
              </DropdownContainer>
              <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
            </NavLinks>
          </>
        ) : null}
      </Nav>
      {isAuthenticated && <Overlay $isOpen={isMenuOpen} onClick={closeMenu} />}
    </>
  );
};

export default Navbar;